import React from 'react';
import { GameState, TableAgainst, TableCard } from '../../game';
import { handTotal } from '../../game/table';
import { formatMoney } from '../../game/presentation';

interface TableChallengeProps {
  game: GameState;
  playerId: string;
  reducedMotion?: boolean;
  localChoosing?: boolean;
  busy?: boolean;
  onChoose: (against: TableAgainst) => void;
  onHit: () => void;
  onStand: () => void;
  onCancel?: () => void;
}

const SUIT_MARK: Record<TableCard['suit'], string> = {
  spades: '♠',
  hearts: '♥',
  diamonds: '♦',
  clubs: '♣',
};

const TableChallenge: React.FC<TableChallengeProps> = ({
  game,
  playerId,
  reducedMotion = false,
  localChoosing = false,
  busy = false,
  onChoose,
  onHit,
  onStand,
  onCancel,
}) => {
  const table = game.table;
  const outcome = table?.outcome ?? null;
  const choosing = !outcome && (game.phase === 'awaiting_table_choice' || localChoosing);
  const playing = !outcome && game.phase === 'awaiting_table' && Boolean(table);
  const current = game.players[game.currentPlayerIndex];
  const isActor = current?.id === playerId;
  const stake = table?.stake ?? game.currentSpinValue ?? 0;
  const doubled = outcome === 'win';
  const playerName = table
    ? game.players.find((player) => player.id === table.playerId)?.name ?? current?.name ?? 'Player'
    : current?.name ?? 'Player';
  let rival = 'House';
  if (table && table.against.type === 'player') {
    const opponentId = table.against.playerId;
    rival = game.players.find((player) => player.id === opponentId)?.name ?? 'Opponent';
  }
  const opponents = game.players.filter((player) => player.id !== (current?.id ?? playerId));
  const playerTotal = table ? handTotal(table.playerHand) : 0;
  const rivalTotal = table && !table.holeHidden ? handTotal(table.opponentHand) : null;

  return (
    <div className="ss-table-overlay" role="dialog" aria-modal="true" aria-labelledby="table-title">
      <div className={`ss-table-felt${outcome ? ` is-${outcome}` : ''}`} aria-live="polite">
        <div className="ss-table-marquee" aria-hidden="true">
          {Array.from({ length: 12 }, (_, index) => (
            <i key={index} style={{ '--i': index } as React.CSSProperties} />
          ))}
        </div>
        <p className="ss-table-kicker">Table Challenge</p>
        <h2 id="table-title">Table 21</h2>
        <ChipStack amount={doubled ? stake * 2 : stake} doubled={doubled} />

        {choosing && (
          <>
            <p className="ss-table-copy">
              {isActor
                ? `Take ${formatMoney(stake)} to the table. Pick a seat.`
                : `${playerName} is picking a seat for ${formatMoney(stake)}.`}
            </p>
            {isActor && (
              <div className="ss-table-seats">
                <button
                  type="button"
                  className="ss-btn ss-btn-primary"
                  disabled={busy}
                  onClick={() => onChoose({ type: 'house' })}
                >
                  House
                </button>
                {opponents.map((player) => (
                  <button
                    key={player.id}
                    type="button"
                    className="ss-btn ss-btn-ghost"
                    disabled={busy}
                    onClick={() => onChoose({ type: 'player', playerId: player.id })}
                  >
                    {player.name}
                  </button>
                ))}
                {onCancel && (
                  <button type="button" className="ss-btn ss-btn-ghost ss-table-skip" disabled={busy} onClick={onCancel}>
                    Keep this spin
                  </button>
                )}
              </div>
            )}
          </>
        )}

        {(playing || outcome) && table && (
          <>
            <Hand
              label={rival}
              cards={table.opponentHand}
              holeHidden={Boolean(table.holeHidden)}
              total={rivalTotal}
              reducedMotion={reducedMotion}
            />
            <Hand
              label={playerName}
              cards={table.playerHand}
              holeHidden={false}
              total={playerTotal}
              reducedMotion={reducedMotion}
              yours
            />
          </>
        )}

        {playing && isActor && (
          <div className="ss-table-acts">
            <button type="button" className="ss-btn ss-btn-primary" disabled={busy} onClick={onStand}>
              Stand
            </button>
            <button type="button" className="ss-btn ss-btn-accent" disabled={busy} onClick={onHit}>
              Hit once
            </button>
          </div>
        )}

        {playing && !isActor && <p className="ss-table-copy">Watching {playerName} — hit once or stand.</p>}

        {outcome === 'win' && <p className="ss-table-result">Double. This spin is now {formatMoney(stake * 2)}.</p>}
        {outcome === 'push' && <p className="ss-table-result">Push. Stake stays {formatMoney(stake)}.</p>}
        {outcome === 'lose' && <p className="ss-table-result">This spin is gone. Round money stays.</p>}
      </div>
    </div>
  );
};

function ChipStack({ amount, doubled }: { amount: number; doubled: boolean }) {
  return (
    <div className={`ss-chips${doubled ? ' is-double' : ''}`} aria-label={`Stake ${formatMoney(amount)}`}>
      <span className="ss-chip" />
      <span className="ss-chip" />
      <span className="ss-chip is-face">{formatMoney(amount)}</span>
      {doubled && <span className="ss-chip-x">×2</span>}
    </div>
  );
}

function Hand({
  label,
  cards,
  holeHidden,
  total,
  reducedMotion,
  yours = false,
}: {
  label: string;
  cards: TableCard[];
  holeHidden: boolean;
  total: number | null;
  reducedMotion: boolean;
  yours?: boolean;
}) {
  return (
    <div className={`ss-hand${yours ? ' is-yours' : ''}`}>
      <div className="ss-hand-meta">
        <span>{label}</span>
        <strong className="ss-hand-total">{total == null ? '?' : total}</strong>
      </div>
      <div className="ss-hand-cards">
        {cards.map((entry, index) => (
          <PlayingCard
            key={`${cardKey(entry)}-${index}`}
            card={entry}
            hidden={holeHidden && index === 1}
            delay={reducedMotion ? 0 : index * 70}
          />
        ))}
      </div>
    </div>
  );
}

function PlayingCard({ card, hidden, delay }: { card: TableCard; hidden: boolean; delay: number }) {
  const red = card.suit === 'hearts' || card.suit === 'diamonds';
  return (
    <div
      className={`ss-card${hidden ? ' is-back' : ''}${red ? ' is-red' : ''}`}
      style={{ animationDelay: `${delay}ms` }}
      aria-hidden={hidden}
    >
      {hidden ? (
        <span className="ss-card-back">21</span>
      ) : (
        <>
          <span className="ss-card-rank">{card.rank}</span>
          <span className="ss-card-suit">{SUIT_MARK[card.suit]}</span>
        </>
      )}
    </div>
  );
}

function cardKey(card: TableCard): string {
  return `${card.rank}:${card.suit}`;
}

export default TableChallenge;
